import { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updatePrices } from '../store/tickerTapeSlice';
import { updateSpreadData } from '../store/spreadsSlice';
import { updateYieldData } from '../store/yieldsSlice';
import { getWsUrl } from '../config/api.config';
import { admitPriceUpdate } from '../utils/session';

const WS_URL = getWsUrl();
const MAX_RECONNECT_ATTEMPTS = 50;
// A tab hidden this long drops its socket (a background tab streamed ~8.6 MB/h
// that nobody saw). On return it reconnects; the Dashboard resubscribes when
// isConnected flips true and the server sends a fresh snapshot.
const HIDDEN_PAUSE_MS = 60 * 1000;

// Batching configuration - collect updates over this window before dispatching
// 200ms gives smoother UI - users said delay is OK, speed is priority
const BATCH_INTERVAL_MS = 200;
const MAX_BATCH_SIZE = 500; // Prevent memory spikes from large ticker lists

export const useWebSocket = () => {
  const ws = useRef(null);
  const dispatch = useDispatch();
  const [isConnected, setIsConnected] = useState(false);
  const lastUpdateRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const isConnecting = useRef(false);
  const handleMessageRef = useRef(null);
  const pausedRef = useRef(false);

  // Batching refs - accumulate updates and dispatch in batches
  const priceBatchRef = useRef({});
  const batchTimeoutRef = useRef(null);
  const batchStartedAtRef = useRef(null); // when the first frame of the batch arrived

  // Flush accumulated price updates to Redux
  const flushPriceBatch = useCallback(() => {
    if (Object.keys(priceBatchRef.current).length > 0) {
      dispatch(updatePrices(priceBatchRef.current, batchStartedAtRef.current ?? undefined));
      priceBatchRef.current = {};
    }
    batchStartedAtRef.current = null;
    batchTimeoutRef.current = null;
  }, [dispatch]);

  // Define handleMessage first, store in ref to avoid dependency issues
  handleMessageRef.current = useCallback((data) => {
    lastUpdateRef.current = Date.now(); // Use ref, no re-render

    switch (data.type) {
      case 'connected':
        // Connection confirmed - no logging in production
        break;

      case 'priceUpdate': {
        // Off the regular session the feed is not meant to push trades, and a
        // frame that arrives anyway must not move the screen (admitPriceUpdate).
        const admitted = admitPriceUpdate(data.data);
        if (admitted && Object.keys(admitted).length > 0) {
          // Batch price updates: merge into accumulated batch
          if (batchStartedAtRef.current === null) batchStartedAtRef.current = Date.now();
          Object.assign(priceBatchRef.current, admitted);

          // Prevent memory spikes: if batch exceeds limit, keep only recent entries
          const batchKeys = Object.keys(priceBatchRef.current);
          if (batchKeys.length > MAX_BATCH_SIZE) {
            const keysToKeep = batchKeys.slice(-MAX_BATCH_SIZE);
            const trimmedBatch = {};
            keysToKeep.forEach(key => {
              trimmedBatch[key] = priceBatchRef.current[key];
            });
            priceBatchRef.current = trimmedBatch;
          }

          // Schedule flush if not already scheduled
          if (!batchTimeoutRef.current) {
            batchTimeoutRef.current = setTimeout(flushPriceBatch, BATCH_INTERVAL_MS);
          }
        }
        break;
      }

      case 'spreadData':
        if (data.data) {
          dispatch(updateSpreadData(data.data));
        }
        break;

      case 'yieldData':
        if (data.data) {
          dispatch(updateYieldData(data.data));
        }
        break;

      case 'dataChange':
        // Data change notification received - handled silently
        break;

      case 'pong':
        // Heartbeat response
        break;

      case 'error':
        console.error('WebSocket error message:', data.message);
        break;

      default:
        // Unknown message type - ignore
        break;
    }
  }, [dispatch, flushPriceBatch]);

  const connect = useCallback(() => {
    // Prevent multiple simultaneous connection attempts
    if (isConnecting.current || (ws.current && ws.current.readyState === WebSocket.OPEN)) {
      return;
    }

    // Stop if too many attempts
    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      // Max attempts reached - prices will update on next page refresh
      return;
    }

    isConnecting.current = true;

    try {
      // Clean up existing connection
      if (ws.current) {
        ws.current.close();
        ws.current = null;
      }

      ws.current = new WebSocket(WS_URL);

      ws.current.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
        isConnecting.current = false;

        // Send initial ping
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
          ws.current.send(JSON.stringify({ type: 'ping' }));
        }
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessageRef.current(data);
        } catch (error) {
          // Silent fail for parse errors
        }
      };

      ws.current.onerror = () => {
        // Don't log - handled in onclose
        setIsConnected(false);
        isConnecting.current = false;
      };

      ws.current.onclose = () => {
        setIsConnected(false);
        isConnecting.current = false;
        ws.current = null;

        // Only reconnect if we haven't exceeded attempts
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          const timeout = Math.min(2000 * Math.pow(1.5, reconnectAttempts.current), 15000);
          reconnectAttempts.current++;

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, timeout);
        }
      };
    } catch (error) {
      // WebSocket connection error - will retry automatically
      setIsConnected(false);
      isConnecting.current = false;
    }
  }, []);

  const subscribeToPrices = useCallback((tickers) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'subscribe',
        tickers: tickers
      }));
    }
  }, []);

  const requestSpreadData = useCallback((tickers) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'getSpreadData',
        tickers: tickers
      }));
    }
  }, []);

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    }
  }, []);

  // Heartbeat interval ref - allows cleanup from multiple places
  const heartbeatIntervalRef = useRef(null);

  // Set up heartbeat - only when connected
  useEffect(() => {
    // Clear any existing interval first (prevents duplicates on re-mount)
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    // Only set up heartbeat when connected
    if (isConnected) {
      heartbeatIntervalRef.current = setInterval(() => {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
          ws.current.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000); // Ping every 30 seconds
    }

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [isConnected]);

  // Connect on mount + watchdog to reconnect if dropped
  useEffect(() => {
    connect();

    // Watchdog: if connection is lost and reconnect attempts exhausted, reset and retry
    const watchdog = setInterval(() => {
      if (pausedRef.current) return;
      if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
        reconnectAttempts.current = 0; // Reset counter
        connect();
      }
    }, 30000); // Check every 30 seconds

    return () => {
      clearInterval(watchdog);
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [connect]);

  // Pause while the tab is hidden; resume when it is shown again.
  useEffect(() => {
    let hideTimer = null;
    const onVisibility = () => {
      if (document.hidden) {
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => {
          pausedRef.current = true;
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          if (ws.current) {
            ws.current.onclose = null; // no auto-reconnect while paused
            ws.current.close();
            ws.current = null;
          }
          isConnecting.current = false;
          setIsConnected(false);
        }, HIDDEN_PAUSE_MS);
      } else {
        clearTimeout(hideTimer);
        hideTimer = null;
        if (pausedRef.current) {
          pausedRef.current = false;
          reconnectAttempts.current = 0;
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    // A tab opened in the background gets no visibilitychange until it is shown.
    if (document.hidden) onVisibility();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(hideTimer);
    };
  }, [connect]);

  return {
    isConnected,
    lastUpdate: lastUpdateRef.current,
    subscribeToPrices,
    requestSpreadData,
    sendMessage
  };
};